import type {
  AccountTransactionRow,
  GetAccountTransactionsOptions,
  TransactionDatePreset,
  TransactionStatusFilter,
} from '../account-transactions.model';
import {
  isUnavailableAccountTransaction,
  TRANSACTION_PAGE_SIZE,
} from '../account-transactions.model';

export const TRANSACTION_DATE_OPTIONS: { label: string; value: TransactionDatePreset }[] = [
  { label: 'All dates', value: 'all' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'Last 12 months', value: '365' },
  { label: 'Custom range', value: 'custom' },
];

/** Cash Advance status query values (AIM `status=`). */
export const DEBT_TRANSACTION_STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'All status', value: 'all' },
  { label: 'Processing', value: 'Processing' },
  { label: 'Scheduled', value: 'Scheduled' },
  { label: 'Missed payment', value: 'Missed' },
  { label: 'Partially complete', value: 'Partial_Complete' },
  { label: 'Complete', value: 'Complete' },
  { label: 'Paused', value: 'Paused' },
];

/** Kashable Loan repayment status query values (AIM `status=`). */
export const LOAN_TRANSACTION_STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'All status', value: 'all' },
  { label: 'Processing', value: 'Processing' },
  { label: 'Missed payment', value: 'Missed' },
  { label: 'Partially complete', value: 'Partial_Complete' },
  { label: 'Complete', value: 'Complete' },
];

export function transactionDay(createdDate: string): Date | null {
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(createdDate.trim());
  if (!ymd) {
    return null;
  }
  return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysAgo(now: Date, days: number): Date {
  const start = startOfDay(now);
  start.setDate(start.getDate() - days);
  return start;
}

export function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function displayTransactionDescription(description: string): string {
  return description.replace(/\s+-\s*$/, '').trim() || description;
}

export function isCustomRangeInvalid(from: Date | null, to: Date | null): boolean {
  if (!from || !to) {
    return false;
  }
  return startOfDay(to).getTime() < startOfDay(from).getTime();
}

export type TransactionQueryState =
  | { kind: 'ready'; options: GetAccountTransactionsOptions }
  | { kind: 'pending' }
  | { kind: 'invalid' };

export function resolveTransactionQuery(
  datePreset: TransactionDatePreset,
  customFrom: Date | null,
  customTo: Date | null,
  status: TransactionStatusFilter,
  includeStatus: boolean,
  now = new Date()
): TransactionQueryState {
  if (datePreset === 'custom') {
    if (!customFrom || !customTo) {
      return { kind: 'pending' };
    }
    if (isCustomRangeInvalid(customFrom, customTo)) {
      return { kind: 'invalid' };
    }
  }
  const options: GetAccountTransactionsOptions = {};
  if (datePreset === '30') {
    options.fromDate = toYmd(daysAgo(now, 30));
    options.toDate = toYmd(startOfDay(now));
  } else if (datePreset === '90') {
    options.fromDate = toYmd(daysAgo(now, 90));
    options.toDate = toYmd(startOfDay(now));
  } else if (datePreset === '365') {
    options.fromDate = toYmd(daysAgo(now, 365));
    options.toDate = toYmd(startOfDay(now));
  } else if (datePreset === 'custom' && customFrom && customTo) {
    options.fromDate = toYmd(startOfDay(customFrom));
    options.toDate = toYmd(startOfDay(customTo));
  }
  if (includeStatus && status && status !== 'all') {
    options.status = status;
  }
  return { kind: 'ready', options };
}

/** Map AIM repayment status query values onto row labels. */
export function repaymentStatusMatchesQuery(rowStatus: string, queryStatus: string): boolean {
  const row = rowStatus.trim().toLowerCase();
  const query = queryStatus.trim().toLowerCase();
  if (query === 'missed') {
    return row.includes('missed');
  }
  if (query === 'partial_complete') {
    return row.includes('partial');
  }
  return row === query;
}

/** Client-side search only. Date and status are applied on the GET. */
export function filterTransactions(
  rows: readonly AccountTransactionRow[],
  options: { search: string }
): AccountTransactionRow[] {
  const query = options.search.trim().toLowerCase();
  if (!query) {
    return [...rows];
  }
  return rows.filter((row) => {
    if (isUnavailableAccountTransaction(row)) {
      return true;
    }
    const haystack = `${row.description} ${row.merchant ?? ''}`.toLowerCase();
    return haystack.includes(query);
  });
}

export function paginateTransactions(
  rows: readonly AccountTransactionRow[],
  page: number,
  pageSize = TRANSACTION_PAGE_SIZE
): {
  rows: AccountTransactionRow[];
  page: number;
  pageCount: number;
  from: number;
  to: number;
  total: number;
} {
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(page, 1), pageCount);
  const start = total === 0 ? 0 : (safePage - 1) * pageSize;
  const slice = rows.slice(start, start + pageSize);
  return {
    rows: slice,
    page: safePage,
    pageCount,
    from: total === 0 ? 0 : start + 1,
    to: start + slice.length,
    total,
  };
}
